// @file Recognises the link-shortcut stub files browsers put in a drag payload.
//
// When a link is dragged between windows (from the address bar, a bookmark, a tab,
// or another app), the browser hands the drop target a tiny shortcut file alongside
// `text/uri-list` — a Windows `.url`, a macOS `.webloc`, or a Linux `.desktop`. The
// file is just a wrapper around the URL, not content anyone wants uploaded, so a
// drop made entirely of these is a link drop.

// Real shortcuts are a few hundred bytes at most. The gate keeps us from reading a
// large file that merely happens to start with a shortcut header.
const SHORTCUT_MAX_BYTES = 4096

// Only web links become link attachments. `javascript:` and `file:` shortcuts are
// left alone so a crafted stub can't turn into an attachment URL.
const HTTP_URL = /^https?:\/\/\S+$/i

const BYTE_ORDER_MARK = /^﻿/
const INI_HEADER = /^\s*\[(InternetShortcut|Desktop Entry)\]/i
const INI_URL = /^[ \t]*URL\s*=\s*(\S+)[ \t]*$/im
const PLIST_URL = /<key>\s*URL\s*<\/key>\s*<string>([^<]+)<\/string>/i
const PLIST_STRING = /<string>([^<]+)<\/string>/i

type InternetShortcutFormat = 'url' | 'webloc'

/**
 * Parses a shortcut's text using the same web-URL policy for both drag sniffing
 * and extension-based upload handling.
 *
 * The optional format hint preserves the older uploader's support for minimal
 * `.url` and `.webloc` files that omit their usual wrapper/header. Without a hint,
 * the contents must identify themselves so arbitrary small text files are not
 * mistaken for shortcuts.
 */
const parseInternetShortcutUrl = (raw: string, format?: InternetShortcutFormat): string | null => {
  const text = raw.replace(BYTE_ORDER_MARK, '')

  // Windows `.url` and Linux `.desktop` are both INI files with a URL= line.
  if (format === 'url' || INI_HEADER.test(text)) {
    const url = text.match(INI_URL)?.[1]
    return url != null && HTTP_URL.test(url) ? url : null
  }

  // macOS `.webloc` is a plist with a URL key.
  if (format === 'webloc' || text.includes('<plist')) {
    const url = (
      text.match(PLIST_URL)?.[1] ?? (format === 'webloc' ? text.match(PLIST_STRING)?.[1] : undefined)
    )?.trim()
    return url != null && HTTP_URL.test(url) ? url : null
  }

  return null
}

const readText = async (file: File): Promise<string | null> =>
  await new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null)
    reader.onerror = () => resolve(null)
    reader.readAsText(file)
  })

/**
 * Returns the URL a dropped file wraps, or null if it isn't a link shortcut.
 *
 * The bytes are sniffed rather than the name or MIME type: browsers report these
 * stubs inconsistently — Chromium on Windows names one `{page title}.download` with
 * type `application/octet-stream`, and others report an empty type.
 */
const readInternetShortcutUrl = async (file: File): Promise<string | null> => {
  if (file.size === 0 || file.size > SHORTCUT_MAX_BYTES) return null
  const text = await readText(file)
  return text == null ? null : parseInternetShortcutUrl(text)
}

/**
 * Returns the URLs of a dropped file set, but only when *every* file is a link
 * shortcut. A mixed drop (a photo plus a shortcut) stays an upload, because we
 * can't drop the photo on the user's behalf.
 */
const readInternetShortcutUrls = async (files: File[]): Promise<string[] | null> => {
  if (files.length === 0) return null
  const urls = await Promise.all(files.map(readInternetShortcutUrl))
  return urls.some((url) => url == null) ? null : (urls as string[])
}

export { parseInternetShortcutUrl, readInternetShortcutUrl, readInternetShortcutUrls }
